import * as React from "react";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { ContextAwareProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireContainer";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { EmployeeRecord, LinkObject } from "@teamprevent/common/model";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";

import { hasQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";

import { values } from "lodash";

import { CompletedDate } from "../../../components/CompletedDate";
import { AnsweredQuestionWithId, FileAnswer, OhCaseDocumentFile, QuestionnaireTypes } from "../../../questionnaires/model";

import { HealthSurveillanceDeclarationSection } from "../../components/HealthSurveillanceDeclarationSection";
import { HealthSurveillanceConfidentialitySection } from "../../components/HealthSurveillanceConfidentialitySection";
import { HealthSurveillancePersonalDetailsSection } from "../../components/HealthSurveillancePersonalDetailsSection";
import { HealthSurveillanceMedicalQuestionsSection } from "../../components/HealthSurveillanceMedicalQuestionsSection";
import { HealthSurveillanceMedicalQuestionsSectionTwo } from "../../components/HealthSurveillanceMedicalQuestionsSectionTwo";
import { HealthSurveillanceWorkingConfinedSpacesSection } from "../../components/HealthSurveillanceWorkingConfinedSpacesSection";
import { HealthSurveillanceNightWorkerSection } from "../../components/HealthSurveillanceNightWorkerSection";
import { HealthSurveillanceFoodHandlerSection } from "../../components/HealthSurveillanceFoodHandlerSection";
import { HealthSurveillanceSkinSection } from "../../components/HealthSurveillanceSkinSection";
import { HealthSurveillanceSkinSectionTwo } from "../../components/HealthSurveillanceSkinSectionTwo";
import { HealthSurveillanceAudiometrySection } from "../../components/HealthSurveillanceAudiometrySection";
import { HealthSurveillanceRespiratorySection } from "../../components/HealthSurveillanceRespiratorySection";
import { HealthSurveillanceHavsSection } from "../../components/HealthSurveillanceHavsSection";
import { HealthSurveillanceIonisingRadiationSection } from "../../components/HealthSurveillanceIonisingRadiationSection";
import { HealthSurveillanceManualHandlingMSKQuestionnaireSection } from "../../components/HealthSurveillanceManualHandlingMSKQuestionnaireSection";
import { HealthSurveillancePitPupQuestionnaireSection } from "../../components/HealthSurveillancePitPupQuestionnaireSection";
import { HealthSurveillanceLAAWorkerSection } from "../../components/HealthSurveillanceLAAWorkerSection";
import { HealthSurveillanceFieldForceSection } from "../../components/HealthSurveillanceFieldForceSection";
import * as questionIds from "../../questions/questionIds";
import { questionConfigsForEmployeeQuestionnaireSelector  } from "../../selectors";
import { CustomMedicalType, MedicalTypeId } from "../../models";
import { HealthSurveillanceOeukSection } from "../../components/HealthSurveillanceOeukSection";
import { OldHealthSurveillanceNightWorkerSection } from "../../components/OldHealthSurveillanceNightWorkerSection";

export const HealthSurveillanceQuestionnaireKey = "HealthSurveillance";

type Props = {
    getQuestionnaire: () => Promise<AnsweredQuestionWithId[]>;
    loadMedicalTypesForQuestionnaire: () => Promise<void>;
    medicalTypesForQuestionnaire: MedicalTypeId[] | undefined;
    customMedicalTypes: CustomMedicalType[] | undefined;
    loadCustomMedicalTypes: () => Promise<void>;
    submitQuestionnaire: (questions: AnsweredQuestionWithId[]) => Promise<boolean>;
    submitInvalidQuestionnaire: () => void;
    navigateToNextPage: () => void;
    /**
     * @deprecated Replace usages with hooks
     * @returns Org levels for dropdowns
     */
    getOrgLevels: () => Promise<LinkObject[]>;
    recordMedicalsSeparately: boolean;
    /**
     * @deprecated Prefer autofill at the source during call to `props.getQuestionnaire`
     */
    autoFill?: true;
    /**
     * @deprecated Only used when props.autoFill === true, see deprecation note for props.autoFill
     */
    employeeRecord?: EmployeeRecord;
    task: TaskModel | undefined;

    fileUploadApi: string;
    onFileUpload?: (file: File) => Promise<string | OhCaseDocumentFile>;
    onDeleteFile: (fileId: string, fileName: string, ohCaseDocumentId: string) => Promise<void>;
    getDownloadLink: ((fileAnswer: FileAnswer) => string) | undefined;
};

export const HealthSurveillanceQuestionnaire: React.FunctionComponent<Props> = ({
    submitInvalidQuestionnaire,
    navigateToNextPage,
    submitQuestionnaire: submitQuestionnaireProp,
    getQuestionnaire,
    loadMedicalTypesForQuestionnaire,
    medicalTypesForQuestionnaire,
    loadCustomMedicalTypes,
    customMedicalTypes,
    ...props
}: Props) => {
    const enableNewNightWorkerQuestions = getFeatureToggles()[FeatureToggleNames.EnableNewNightWorkerQuestions];

    const submitQuestionnaire = React.useCallback(async (submittedQuestions: AnsweredQuestionWithId[]): Promise<boolean> => {
        const trimAnswerAndUpdateEmployeeQuestion = (questionId: string, questions: AnsweredQuestionWithId[]) => {

            const question = questions.find(q => q.id === questionId);

            if (question) {
                question.answer = question.answer?.toString()?.trim();
            }
        };

        trimAnswerAndUpdateEmployeeQuestion(questionIds.Title, submittedQuestions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.FirstName, submittedQuestions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.LastName, submittedQuestions);

        if (submittedQuestions.some(q => q.isValid !== true && q.isRemovedByUser !== true)) {
            submitInvalidQuestionnaire();
            return false;
        } else {
            const submitted = await submitQuestionnaireProp(submittedQuestions);
            navigateToNextPage();
            return submitted;
        }
    }, [navigateToNextPage, submitInvalidQuestionnaire, submitQuestionnaireProp]);
    const [questions, setQuestions] = React.useState<AnsweredQuestionWithId[] | null>(null);
    const readonly = questions ? questions?.length > 0 : null;
    const loadQuestionnaire = React.useCallback(async () => {
        setQuestions(null);
        const result = await getQuestionnaire();
        const questionnaireComplete = result?.length > 0;
        setQuestions(result);
        if (questionnaireComplete !== true) {
            await Promise.all([
                loadCustomMedicalTypes(),
                loadMedicalTypesForQuestionnaire()
            ]);
        }
        return result;
    }, [getQuestionnaire, loadCustomMedicalTypes, loadMedicalTypesForQuestionnaire]);
    const questionConfigs = React.useMemo<CustomQuestionConfig[] | null>(() => {
        if (readonly === true) {
            return questions!.map(q => ({ id: q.id, questionText: q.question }));
        } else if (readonly === false && medicalTypesForQuestionnaire && customMedicalTypes) {
            return questionConfigsForEmployeeQuestionnaireSelector(medicalTypesForQuestionnaire, customMedicalTypes);
        }

        return null;
    }, [medicalTypesForQuestionnaire, questions, readonly, customMedicalTypes]);
    const getRecordMedicalsSeparatelyAnswer = (): boolean => {
        const question = questions?.find(q => q.id === questionIds.HideEmployeeQuestionsAndRecordMedicalSeparately);

        if (!question)
        {
            return false;
        }

        return question.answer === MultiButtonQuestion.YesOption;
    };
    const removeMedicalSections = getRecordMedicalsSeparatelyAnswer();

    const hasOldNightWorkerQuestions = React.useMemo(() => (hasQuestionWithId(values(questions || {}), questionIds.OfficeDuties) !== undefined), [questions]);

    const renderNightWorkerSection = () => {
        // This readonly/oldQuestions check will need to stay to support legacy data
        if (readonly)
        {
            if (hasOldNightWorkerQuestions) {
                // eslint-disable-next-line deprecation/deprecation
                return <OldHealthSurveillanceNightWorkerSection questionConfigs={questionConfigs} />;
            }
            else
            {
                return <HealthSurveillanceNightWorkerSection questionConfigs={questionConfigs} />;
            }
        }

        if (enableNewNightWorkerQuestions) {
            return <HealthSurveillanceNightWorkerSection questionConfigs={questionConfigs} />;
        }

        // eslint-disable-next-line deprecation/deprecation
        return <OldHealthSurveillanceNightWorkerSection questionConfigs={questionConfigs} />;
    };

    return (
        <>
            {readonly !== null && <CompletedDate readonly={readonly} task={props.task} />}
            <ContextAwareProgressBarQuestionnaireContainer
                readonly={readonly === true}
                loadQuestionnaire={loadQuestionnaire}
                submitQuestionnaire={submitQuestionnaire}
                questionnaireKey={HealthSurveillanceQuestionnaireKey}
                questionnaireType={QuestionnaireTypes.Other}
            >
                {
                    questionConfigs && (<>
                        <HealthSurveillanceConfidentialitySection questionConfigs={questionConfigs} />
                        <HealthSurveillancePersonalDetailsSection
                            questionConfigs={questionConfigs}
                            getOrgLevels={props.getOrgLevels}
                            autoFill={props.autoFill}
                            employeeRecord={props.employeeRecord} />
                        <HealthSurveillanceMedicalQuestionsSection
                            questionConfigs={questionConfigs}
                            recordMedicalsSeparately={props.recordMedicalsSeparately} />
                        {!removeMedicalSections &&
                            <HealthSurveillanceMedicalQuestionsSectionTwo
                                questionConfigs={questionConfigs}
                                fileUploadApi={props.fileUploadApi}
                                onFileUpload={props.onFileUpload}
                                onDeleteFile={props.onDeleteFile}
                                getDownloadLink={props.getDownloadLink}
                            />
                        }
                        {/*
                            Each of these sections must be rendered in their own if statement so that they remain direct siblings of all other sections.
                            When each section uses ContextAwareProgressBarQuestionnaireSection instead of OldProgressBarQuestionnaireSectionUnconnected
                            then I think this will be fixed 🤞
                        */}
                        {!removeMedicalSections &&
                            <HealthSurveillanceWorkingConfinedSpacesSection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            renderNightWorkerSection()
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceFoodHandlerSection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceSkinSection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceSkinSectionTwo questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceAudiometrySection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceRespiratorySection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceHavsSection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceIonisingRadiationSection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceManualHandlingMSKQuestionnaireSection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillancePitPupQuestionnaireSection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceLAAWorkerSection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceFieldForceSection questionConfigs={questionConfigs} />
                        }
                        {!removeMedicalSections &&
                            <HealthSurveillanceOeukSection questionConfigs={questionConfigs} />
                        }
                        <HealthSurveillanceDeclarationSection questionConfigs={questionConfigs} />
                    </>)
                }
            </ContextAwareProgressBarQuestionnaireContainer>
        </>
    );
};
